<template >
  <div name="documents">
    <div class="flex justify-between mb-4">
      <h2 class="text-gray-500 uppercase">Resources</h2>
      <button
        @click="addNew()"
        type="button"
        class="text-action-blue opacity-75 cursor-pointer hover:opacity-50 wide focus:outline-none"
      >
        <i class="fas fa-plus-circle mr-2 text-xl"></i>
      </button>
    </div>

    <div class="table-wrapper">
      <div v-if="errored" class="empty">
        <p>
          We are not able to retrieve this information at the moment, please try
          back later
        </p>
      </div>

      <div id="content-area" class="w-full mx-auto" v-else>
        <div
          style="width: 100%; text-align: center"
          class="spin-holder mt-4"
          v-if="loading"
        >
          <i class="spinner fa fa-spinner fa-spin"></i>
          <p>Loading Documents</p>
        </div>

        <div v-else>
          <div v-if="componentLoaded">
            <div v-for="list in orderedDocuments" :key="list.section">
              <h3 class="w-full bg-gray-100 p-2">{{ list.section }}</h3>
              <table class="table-wrapper w-95p bg-white rounded shadow-sm">
                <tbody>
                  <tr v-if="list.data.length === 0">
                    <td><span class="py-2"> No data</span></td>
                  </tr>
                  <tr v-else v-for="(item, index) in list.data" :key="index">
                    <td
                      @click="displayResource(item)"
                      class="cursor-point w-1/12 pl-2"
                    >
                      <i class="icon fas" v-bind:class="showIcon(item)"></i>
                    </td>

                    <td
                      @click="displayResource(item)"
                      class="cursor-pointer w-7/12"
                    >
                      {{ item.title }}
                    </td>

                    <td v-if="hasNoLesson" @click="displayResource(item)">
                      {{ getDate(item) }}
                    </td>

                    <td
                      class="text-xs cursor-pointer text-blue-400 w-1/12 left"
                    >
                      <template v-if="isOwnedDocument(item)">
                        <i
                          class="fas fa-ellipsis-h hover:opacity-75 pl-4"
                          @click="selectToDelete(item)"
                        ></i>

                        <transition
                          enter-active-class="transition duration-300 ease-out transform"
                          nter-class="-translate-y-3 scale-95 opacity-0"
                          enter-to-class="translate-y-0 scale-100 opacity-100"
                          leave-active-class="transition duration-150 ease-in transform"
                          leave-class="translate-y-0 opacity-100"
                          leave-to-class="-translate-y-3 opacity-0"
                        >
                          <div v-show="showDelete(item)" class="absolute pt-2">
                            <div
                              class="relative py-1 bg-white border border-gray-200 rounded-md shadow-xl"
                            >
                              <div
                                class="absolute top-0 w-4 h-4 origin-center transform rotate-45 translate-x-5 -translate-y-2 bg-white border-t border-l border-gray-200 rounded-sm pointer-events-none"
                              ></div>
                              <div class="relative">
                                <a
                                  class="block w-full px-4 py-2 font-medium text-gray-700 whitespace-no-wrap hover:bg-gray-100 focus:outline-none hover:text-gray-900 focus:text-gray-900 focus:shadow-outline transition duration-300 ease-in-out"
                                  @click="displayResource(item)"
                                  >View</a
                                >
                                <a
                                  @click="deleteResource(item)"
                                  class="block w-full px-4 py-2 font-medium text-red-500 whitespace-no-wrap hover:bg-gray-100 focus:outline-none hover:text-gray-900 focus:text-gray-900 focus:shadow-outline transition duration-300 ease-in-out"
                                  >Delete</a
                                >
                                <a
                                  @click="cancelSelect()"
                                  class="block w-full px-4 py-2 font-medium text-blue-500 whitespace-no-wrap hover:bg-gray-100 focus:outline-none hover:text-gray-900 focus:text-gray-900 focus:shadow-outline transition duration-300 ease-in-out"
                                  >Cancel</a
                                >
                              </div>
                            </div>
                          </div>
                        </transition>
                      </template>
                      <template v-else>
                        <i
                          v-if="!currentUser.isSubscriber"
                          class="fas fa-lock text-yellow-500 text-lg pl-2"
                        ></i>
                        <a v-else class="ml-4" @click="displayResource(item)"
                          >View</a
                        >
                      </template>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <p class="m-5 mx-12" v-else>
            We could not find any data for your given selection.
          </p>
        </div>
      </div>
    </div>

    <modalfile
      :url="loadFilePath()"
      :title="selectedDocument.title"
      :format="selectedFormat"
      @close="showModalFile = false"
      v-if="selectedDocument != undefined && showModalFile"
      v-show="showModalFile"
    ></modalfile>

    <modalvideo
      :url="loadVideoPath()"
      @close="showModalVideo = false"
      v-if="selectedDocument != undefined && showModalVideo"
      v-show="showModalVideo"
    ></modalvideo>

    <ModalSlide :show="showModalAddition" @closeModal="closeAddResource">
      <template v-slot:header-icon>
        <i class="fas fa-plus"></i>
      </template>
      <template v-slot:header-title>
        Add New Resource
        <div class="text-xs">Enter details for new resource file.</div>
      </template>
      <template v-slot:body>
        <ResourceAdd
          :course="course"
          :lesson="lesson"
          :displayKey="displayKey"
          @resource-added="resourceAdded"
        ></ResourceAdd>
      </template>
    </ModalSlide>
  </div>
</template>

<script>
import moment from "moment";
import { mapGetters } from "vuex";
import { ResourceService } from "@/services/resource.service";
import { fileResourceMixin } from "@/mixins/file-ResourceMixin.js";

const ModalSlide = () => import("@/components/ModalSlide.vue");
const ResourceAdd = () => import("@/components/library/TheResourceAdd.vue");

const modalfile = () => import("@/components/ModalFile.vue");
const modalvideo = () => import("@/components/ModalVideo.vue");

export default {
  name: "documents",
  mixins: [fileResourceMixin],
  props: {
    course: Object,
    lesson: { type: Object, required: false },
    displayKey: { type: String, required: false },
  },
  computed: {
    ...mapGetters({
      currentUser: "currentUser",
      currentAccess: "currentAccess",
    }),
    showForSchool() {
      return this.currentUser.isSchool == true;
    },
    orderedDocuments() {
      var list = [
        {
          section: "Documents",
          data: this.documentList,
        },
      ];

      if (this.diagramList.length > 0) {
        list.push({
          section: "Diagrams",
          data: this.diagramList,
        });
      }

      return list;
    },
    componentLoaded() {
      return this.orderedDocuments.length > 0;
    },
    hasFile() {
      return this.file.name != undefined && this.file.name.length > 3;
    },
    hasResourceModule() {
      return this.currentAccess.resourceAccess;
    },
    schoolName() {
      return this.currentUser.schoolName;
    },
    hasNoLesson() {
      return this.lesson === undefined;
    },
    diagramList() {
      var vm = this;
      let docs = vm.resources.filter(function (r) {
        return r.format === "diagram" && r.display_key === vm.displayKey;
      });

      return _.orderBy(docs, [vm.sortBy], [vm.sortDirection]);
    },
    documentList() {
      var vm = this;
      var docs = vm.resources.filter(function (r) {
        return r.format != "diagram";
      });

      if (!this.hasNoLesson) {
        docs = docs.filter(function (r) {
        return r.display_key === vm.displayKey;
      });
      }

      return _.orderBy(docs, [vm.sortBy], [vm.sortDirection]);
    },
  },
  data() {
    return {
      loading: true,
      errored: false,

      selectedDocument: {},
      showModalFile: false,
      showModalVideo: false,
      showModalAddition: false,
      resources: [],
      file: {},
      title: "",
      isMenuVisible: false,
      documentTypes: [],
      selectedFormat: "document",
      search: "",
    };
  },
  created() {
    this.loading = true;
  },
  mounted() {
    this.searchResources();
  },
  methods: {
    searchResources() {
      if (this.hasNoLesson) {
        this.unitResources();
      } else {
        this.lessonResources();
      }
    },
    unitResources() {
      this.loading = true;
      ResourceService.getForUnit(this.course.id)
        .then((response) => this.buildResources(response.data))
        .catch((err) => {
          console.log("resources " + err);
          this.errored = true;
        })
        .finally(() => (this.loading = false));
    },
    lessonResources() {
      this.loading = true;
      ResourceService.getForLesson(this.lesson.id)
        .then((response) => this.buildResources(response.data))
        .catch((err) => {
          console.log("resources " + err);
          this.errored = true;
        })
        .finally(() => (this.loading = false));
    },
    buildResources(data) {
      this.resources = data.data;
    },
    showIcon(document) {
      const name = document.url;
      let fileExt = name.split(".").pop();
      if (fileExt == "doc") {
        return "fa-file-word";
      } else if (fileExt == "pdf") {
        return "fa-file-pdf";
      } else if (["png", "jpg", "gif"].includes(fileExt)) {
        return "fa-file-image";
      } else if (fileExt == "csv") {
        return "fa-file-csv";
      } else if (["xls", "xlsx"].includes(fileExt)) {
        return "fa-file-exel";
      } else {
        return "fa-file-video";
      }
    },
    displayResource(document) {
      this.selectedDocument = document;
      this.isMenuVisible = false;
      if (document.type == "video") {
        this.showModalVideo = true;
      } else {
        this.showModalFile = true;
      }
    },
    isOwnedDocument(document) {
      let owned = (document.school_id === null || document.school_id === undefined) ? 1 : document.school_id;
      return owned === this.currentUser.id;
    },
    loadFilePath() {
      let url = this.selectedDocument.url;
      if (this.selectedDocument.format === "diagram") {
        this.selectedFormat = "diagrams";
      } else {
        this.selectedFormat = this.selectedDocument.format;
      }

      return this.isOrganisationOrOwnedDocument(this.selectedDocument)
        ? url
        : this.selectedDocument.folder + "/" + url;
    },
    isOrganisationOrOwnedDocument(document) {
      return ["organisation", "school"].includes(document.type.key_value);
    },
    loadVideoPath() {
      return this.selectedDocument.file;
    },
    addNew() {
      this.showModalAddition = true;
    },
    closeAddResource() {
      this.showModalAddition = false;
    },
    resourceAdded() {
      this.closeAddResource();
      this.searchResources();
    },
    selectToDelete(document) {
      this.selectedDocument = document;
      this.isMenuVisible = true;
    },
    showDelete(document) {
      return (
        this.isMenuVisible === true &&
        this.selectedDocument.identifier === document.identifier
      );
    },
    deleteResource(document) {
      var vm = this;

      let payload = {
        identifier: document.identifier,
      };

      ResourceService.delete(payload)
        .then((response) => {
          vm.searchResources();
          vm.cancelSelect();
        })
        .catch((err) => {
          console.log(err);
          vm.failure(err);
        });
    },
    failure(err) {
      this.cancelSelect();
      throw new Error(`Resources failure error: ${err}`);
    },
    cancelSelect() {
      this.isMenuVisible = false;
      this.selectedDocument.identifier = undefined;
    },
    getDate(object) {
      return this.isOwnedDocument(object)
        ? moment(object.updated_at).format(moment.HTML5_FMT.DATE)
        : "-";
    },
  },
  components: {
    modalfile,
    modalvideo,
    ResourceAdd,
    ModalSlide,
  },
};
</script>

<style>
i.icon {
  font-size: 18px;
  padding-left: 4px;
  padding-top: 4px;
}
</style>


